// crewbit-admin/src/components/money/MoneyTaxAdmin.tsx
//
// Top-level wrapper for /money/tax (Phase C screen 7). Composes:
//   PageHeader (with Generate summary primary action)
//   → SetupHstBanner (conditional on hst_number / tax_mode === 'exempt')
//   → TaxBoxStrip (hero 4-cell; hides on tax_mode='exempt')
//   → QuickMethodNudgeBanner (conditional on tax_mode === 'charge')
//   → 2-col grid: T2125ProgressCard | T4AVendorsCard
//   → RemittanceLogTable
//   → MarkFiledPanel + ReopenRemittancePanel + GenerateSummaryPanel
//
// URL state per §2.6:
//   ?action=mark-filed[&remittance_id=X]    → MarkFiledPanel
//   ?action=reopen&remittance_id=X          → ReopenRemittancePanel (owner)
//   ?action=generate-summary                → GenerateSummaryPanel
//
// Refresh policy is OWNED by the hooks — useFinancialSummary,
// useTaxSummaryDetail, useRemittanceLog each install Page Visibility +
// MONEY_MUTATION_EVENT listeners. This wrapper MUST NOT add its own.
//
// Spec docs/references/MONEY_MODULE_SPEC_v1.md §Tax.7.2.

import { useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FilePlus, AlertCircle, RefreshCw } from 'lucide-react';
import { PageHeader } from '../common/PageHeader';
import SettingsGearLink from './settings/SettingsGearLink';
import { Skeleton } from '../common/Skeleton';
import { admin, palette, radii, shadow, spacing } from '../../styles/tokens';
import { useFinancialSummary } from '../../hooks/useFinancialSummary';
import { useTaxSummaryDetail } from '../../hooks/useTaxSummaryDetail';
import { useRemittanceLog, type RemittanceLogRow } from '../../hooks/useRemittanceLog';
import { useActiveBusinessStore } from '../../hooks/useActiveBusiness';
import { useAuthStore } from '../../stores/authStore';
import { useBusinessFinancialSettings } from '../../hooks/useBusinessFinancialSettings';
import type { TaxType } from '../../lib/taxMutations';
import { TaxBoxStrip } from './TaxBoxStrip';
import { SetupHstBanner } from './SetupHstBanner';
import { QuickMethodNudgeBanner } from './QuickMethodNudgeBanner';
import { T2125ProgressCard } from './T2125ProgressCard';
import { T4AVendorsCard } from './T4AVendorsCard';
import { RemittanceLogTable } from './RemittanceLogTable';
import { MarkFiledPanel } from './MarkFiledPanel';
import { ReopenRemittancePanel } from './ReopenRemittancePanel';
import { GenerateSummaryPanel } from './GenerateSummaryPanel';

const TAX_TYPE: TaxType = 'hst';

const pageStyle: React.CSSProperties = {
  padding: spacing.xl,
  background: admin.bg,
  minHeight: '100vh',
};

export function MoneyTaxAdmin() {
  const navigate = useNavigate();
  const businessId = useActiveBusinessStore((s) => s.activeBusinessId);
  const isOwner = useAuthStore((s) => s.user?.role === 'owner');
  // §Settings.HstNumberDuplication: hst_number is canonical on business_financial_settings
  // (Migration 271 dropped businesses.hst_number). Read via useBusinessFinancialSettings.
  const bfs = useBusinessFinancialSettings(businessId);

  const summary = useFinancialSummary({ period: 'qtd' });
  const periodStart = summary.data?.period.start ?? null;
  const periodEnd = summary.data?.period.end ?? null;
  const taxMode = summary.data?.tax_summary.tax_mode ?? 'charge';
  const periodLabel = summary.data?.tax_summary.quarter_label ?? '';

  const detail = useTaxSummaryDetail({ periodStart, periodEnd, taxType: TAX_TYPE });
  const log = useRemittanceLog({ businessId, taxType: TAX_TYPE, limit: 25 });

  const [searchParams, setSearchParams] = useSearchParams();
  const action = searchParams.get('action');
  const remittanceIdParam = searchParams.get('remittance_id');

  const markFiledOpen = action === 'mark-filed';
  const reopenOpen = action === 'reopen' && !!remittanceIdParam && isOwner;
  const generateSummaryOpen = action === 'generate-summary';

  const remittanceForPanel = useMemo<RemittanceLogRow | null>(() => {
    if (!remittanceIdParam || !log.data) return null;
    return log.data.find((r) => r.id === remittanceIdParam) ?? null;
  }, [remittanceIdParam, log.data]);

  useEffect(() => {
    document.title = 'CrewBit · Tax';
  }, []);

  const closePanels = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('action');
    next.delete('remittance_id');
    setSearchParams(next);
  };

  const openMarkFiledForRow = (row: RemittanceLogRow) => {
    const next = new URLSearchParams(searchParams);
    next.set('action', 'mark-filed');
    next.set('remittance_id', row.id);
    setSearchParams(next);
  };
  const openReopenForRow = (row: RemittanceLogRow) => {
    const next = new URLSearchParams(searchParams);
    next.set('action', 'reopen');
    next.set('remittance_id', row.id);
    setSearchParams(next);
  };
  const openGenerateSummary = () => {
    const next = new URLSearchParams(searchParams);
    next.set('action', 'generate-summary');
    next.delete('remittance_id');
    setSearchParams(next);
  };
  const transitionGenerateSummaryToMarkFiled = () => {
    const next = new URLSearchParams(searchParams);
    next.set('action', 'mark-filed');
    next.delete('remittance_id');
    setSearchParams(next);
  };

  const handleRowClick = (row: RemittanceLogRow) => {
    if (row.status === 'open' || row.status === 'reopened') {
      openMarkFiledForRow(row);
    } else if ((row.status === 'filed' || row.status === 'amended') && isOwner) {
      openReopenForRow(row);
    }
  };

  const generateSummaryButton = (
    <button
      type="button"
      onClick={openGenerateSummary}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '8px 16px',
        borderRadius: radii.sm,
        border: 'none',
        background: palette.accent,
        color: palette.white,
        fontSize: 13,
        fontWeight: 600,
        fontFamily: 'inherit',
        cursor: 'pointer',
      }}
    >
      <FilePlus size={14} strokeWidth={2.25} />
      Generate summary
    </button>
  );

  const hstNumberMissing = !bfs.data?.hstNumber;
  const isExempt = taxMode === 'exempt';
  const showSetupBanner = hstNumberMissing || isExempt;

  // Use the row's period when MarkFiled was opened from a row click;
  // otherwise fall back to the current QTD period from FinancialSummary.
  const markFiledPeriodStart = remittanceForPanel?.period_start ?? periodStart ?? '';
  const markFiledPeriodEnd = remittanceForPanel?.period_end ?? periodEnd ?? '';
  const markFiledTaxMode = taxMode;
  const markFiledLabel = remittanceForPanel
    ? `${formatPeriodShort(remittanceForPanel.period_start, remittanceForPanel.period_end)}`
    : periodLabel;

  const markFiledPrefill = remittanceForPanel
    ? {
        amountCollected: remittanceForPanel.amount_collected,
        amountItc: remittanceForPanel.amount_itc,
        amountOwed: remittanceForPanel.amount_owed,
      }
    : detail.data
      ? {
          amountCollected: detail.data.box_105_hst_collected ?? 0,
          amountItc: detail.data.box_108_itcs ?? 0,
          amountOwed: detail.data.box_109_net ?? 0,
          quickMethodRemittanceRate: detail.data.quick_method_remittance_rate ?? null,
          quickMethodCredit: detail.data.quick_method_credit ?? null,
        }
      : undefined;

  const panels = (
    <>
      <MarkFiledPanel
        isOpen={markFiledOpen}
        onClose={closePanels}
        businessId={businessId}
        taxType={TAX_TYPE}
        periodStart={markFiledPeriodStart}
        periodEnd={markFiledPeriodEnd}
        periodLabel={markFiledLabel}
        taxMode={markFiledTaxMode}
        prefill={markFiledPrefill}
      />
      <ReopenRemittancePanel
        isOpen={reopenOpen}
        onClose={closePanels}
        remittance={remittanceForPanel}
      />
      <GenerateSummaryPanel
        isOpen={generateSummaryOpen}
        onClose={closePanels}
        periodStart={periodStart}
        periodEnd={periodEnd}
        periodLabel={periodLabel}
        taxType={TAX_TYPE}
        onMarkFiled={transitionGenerateSummaryToMarkFiled}
      />
    </>
  );

  // Branch 1 — initial load.
  if (summary.isLoading && !summary.data) {
    return (
      <div style={pageStyle}>
        <PageHeader title="Tax" back={{ to: '/money', label: 'Money' }} primaryAction={generateSummaryButton} secondaryActions={<SettingsGearLink />} />
        <div style={{ marginBottom: spacing.md }}>
          <Skeleton width={420} height={36} />
        </div>
        <div
          style={{
            background: palette.white,
            border: `1px solid ${admin.border}`,
            borderRadius: admin.cardRadius,
            padding: spacing.md,
            boxShadow: shadow.card,
          }}
        >
          <Skeleton width="100%" height={120} />
        </div>
        {panels}
      </div>
    );
  }

  // Branch 2 — initial error.
  if (summary.error && !summary.data) {
    return (
      <div style={pageStyle}>
        <PageHeader title="Tax" back={{ to: '/money', label: 'Money' }} primaryAction={generateSummaryButton} secondaryActions={<SettingsGearLink />} />
        <div
          style={{
            background: palette.amberWash,
            border: `1px solid ${palette.amber}`,
            borderRadius: admin.cardRadius,
            padding: spacing.lg,
            display: 'flex',
            alignItems: 'center',
            gap: spacing.md,
          }}
        >
          <AlertCircle size={20} color={palette.amber} strokeWidth={2} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: palette.amber }}>
              We couldn&apos;t load your tax summary.
            </div>
            <div style={{ fontSize: 13, color: palette.amber, marginTop: 4 }}>
              Check your connection and try again.
            </div>
          </div>
          <button
            type="button"
            onClick={() => void summary.refetch()}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 14px',
              borderRadius: radii.sm,
              border: 'none',
              background: palette.amber,
              color: palette.white,
              fontSize: 13,
              fontWeight: 600,
              fontFamily: 'inherit',
              cursor: 'pointer',
            }}
          >
            <RefreshCw size={13} />
            Retry
          </button>
        </div>
        {panels}
      </div>
    );
  }

  // Branch 3 — happy path.
  return (
    <div style={pageStyle}>
      <PageHeader
        title="Tax" back={{ to: '/money', label: 'Money' }}
        description={periodLabel ? `${TAX_TYPE.toUpperCase()} · ${periodLabel}` : undefined}
        primaryAction={generateSummaryButton}
        secondaryActions={<SettingsGearLink />}
      />

      {showSetupBanner && (
        <SetupHstBanner
          exempt={isExempt}
          onClick={() => navigate('/settings/tax')}
        />
      )}

      <TaxBoxStrip detail={detail.data} isLoading={detail.isLoading} />

      {!isExempt && (
        <QuickMethodNudgeBanner
          taxMode={taxMode}
          onClick={() => navigate('/settings/tax')}
        />
      )}

      {!isExempt && summary.data && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: spacing.md,
            marginBottom: spacing.md,
          }}
        >
          <T2125ProgressCard
            progress={summary.data.t2125_progress}
            onClick={() => navigate('/money/expenses')}
          />
          <T4AVendorsCard vendors={detail.data?.t4a_vendors ?? null} />
        </div>
      )}

      <RemittanceLogTable
        rows={log.data}
        isLoading={log.isLoading}
        onRowClick={handleRowClick}
      />

      {panels}
    </div>
  );
}

function formatPeriodShort(start: string, end: string): string {
  const s = new Date(start);
  const e = new Date(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return '—';
  const opts: Intl.DateTimeFormatOptions = { month: 'short' };
  return `${s.toLocaleDateString('en-CA', opts)}–${e.toLocaleDateString('en-CA', { ...opts, year: 'numeric' })}`;
}
